'use client'

import { useTranslations } from '@/lib/next-shim'
import { AcademicCapIcon, MapPinIcon, ExclamationTriangleIcon, UserIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { Civility, Address, Maybe, TeleconsultationTraining, UserInfo } from '@/core/graphql/types'

type ProfessionalCardStaff = {
  userInfo: Pick<UserInfo, 'name' | 'surname'>
  status: string
  civility?: Maybe<Civility>
  title?: Maybe<string>
  cabinetAddresses?: Array<Maybe<Address>>
  teleconsultationTraining?: Maybe<TeleconsultationTraining>
  conflictOfInterest?: Maybe<string>
  speciality?: Maybe<string>
  avatarUrl?: string
}

interface ProfessionalCardProps {
  staff: ProfessionalCardStaff
  compact?: boolean
  className?: string
}

export function ProfessionalCard({ staff, compact = false, className }: ProfessionalCardProps) {
  const t = useTranslations()

  const { userInfo, status, civility, title, cabinetAddresses = [], teleconsultationTraining, conflictOfInterest } = staff

  const fullName = `${civility ? t(`enum.Civility.${civility}`) + ' ' : ''}${userInfo.name} ${userInfo.surname}`

  return (
    <div className={clsx('bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow', className)}>
      <div className={clsx('p-4 sm:p-5', compact ? 'space-y-2' : 'space-y-4')}>
        {/* Header: Avatar + Name + Title */}
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {staff.avatarUrl ? (
              <img src={staff.avatarUrl} alt={fullName} className="h-16 w-16 rounded-full object-cover border-2 border-primary/10" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-primary/5 flex items-center justify-center border-2 border-primary/10">
                <UserIcon className="h-8 w-8 text-primary/40" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 truncate">{fullName}</h3>
            <p className="text-sm font-medium text-primary uppercase tracking-wider">{title || status}</p>
            {staff.speciality && <p className="text-sm text-gray-600 truncate">{staff.speciality}</p>}
          </div>
        </div>

        {!compact && (
          <>
            {/* Cabinet Addresses */}
            {cabinetAddresses.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-600 uppercase flex items-center tracking-widest">
                  <MapPinIcon className="h-3 w-3 mr-1" />
                  {t('ProfessionalCard.cabinet')}
                </h4>
                <div className="space-y-2">
                  {cabinetAddresses
                    .filter((addr): addr is Address => addr != null)
                    .map((addr, idx: number) => (
                      <div key={idx} className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 border border-gray-100">
                        {addr.description}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Training */}
            {teleconsultationTraining && teleconsultationTraining.type !== 'NONE' && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-600 uppercase flex items-center tracking-widest">
                  <AcademicCapIcon className="h-3 w-3 mr-1" />
                  {t('ProfessionalCard.training')}
                </h4>
                <div className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                  {t(`enum.TrainingType.${teleconsultationTraining.type}`)}
                  {teleconsultationTraining.description && ` : ${teleconsultationTraining.description}`}
                </div>
              </div>
            )}

            {/* Conflict of Interest */}
            {conflictOfInterest && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600 mr-2 mt-0.5" />
                  <div className="text-xs text-yellow-800">
                    <span className="font-bold">{t('ProfessionalCard.conflictOfInterest')}: </span>
                    {conflictOfInterest}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
